/**
 * Chart data transformations — maps backend chart_config + flat data rows
 * into the series shape that UniversalChart expects.
 *
 * Backend contract:
 *   {
 *     chart_config: {
 *       default_chart_style: ChartStyle,
 *       x_axis: { key, label?, type? },
 *       y_axis: [{ key, label }, ...],
 *     },
 *     data: [{ <x_axis.key>, <y_axis[0].key>, <y_axis[1].key>, ... }, ...]
 *   }
 *
 * Each y_axis entry becomes a separate series. For each series, data is a
 * list of { name, y } points where `name` is the x-axis value and `y` is the
 * numeric value of that series' column for that row.
 */

import {formatToCustomDateString} from 'src/utils/analytics-genesis';
import type {ChartAxis, ChartConfig} from 'src/types/byod';

// ---------------------------------------------------------------------------
// x_axis normalization — backend may send a single object or an array.
// Series builders always need a single ChartAxis.
// ---------------------------------------------------------------------------

function normalizeXAxis(
  xAxis: ChartAxis | ChartAxis[] | undefined,
): ChartAxis | undefined {
  if (!xAxis) {
    return undefined;
  }
  if (Array.isArray(xAxis)) {
    return xAxis[0];
  }
  return xAxis;
}

// ---------------------------------------------------------------------------
// Date granularity detection
// ---------------------------------------------------------------------------

/**
 * Inspects the raw x-axis date values and returns the best moment format string.
 *
 * Granularity rules (checked in order):
 *  1. Any value has non-midnight HH:mm → sub-daily
 *     - minutes vary   → `'MMM D, h:mm A'`  (e.g. "May 7, 3:15 PM")
 *     - minutes all :00 → `'MMM D, h A'`     (e.g. "May 7, 3 PM")
 *  2. All day-of-month are '01' → monthly   → `'MMM YYYY'`
 *  3. Otherwise daily                       → `'MMM D, YYYY'`
 */
function detectDateFormat(
  rows: ReadonlyArray<Record<string, unknown>>,
  xKey: string,
): string {
  const values: string[] = [];
  for (const row of rows) {
    const v = row[xKey];
    if (typeof v === 'string') {
      values.push(v);
    }
  }

  if (values.length < 2) {
    return 'MMM D, YYYY';
  }

  let hasNonMidnightTime = false;
  let hasNonZeroMinute = false;
  let allFirstOfMonth = true;
  let hasIsoDayMatch = false;

  for (const v of values) {
    const timeMatch = v.match(/(\d{2}):(\d{2})(?::(\d{2}))?/);
    if (timeMatch) {
      if (timeMatch[1] !== '00' || timeMatch[2] !== '00') {
        hasNonMidnightTime = true;
      }
      if (timeMatch[2] !== '00') {
        hasNonZeroMinute = true;
      }
    }

    const dayMatch = v.match(/\d{4}-\d{2}-(\d{2})/);
    if (dayMatch) {
      hasIsoDayMatch = true;
      if (dayMatch[1] !== '01') {
        allFirstOfMonth = false;
      }
    }
  }

  if (hasNonMidnightTime) {
    return hasNonZeroMinute ? 'MMM D, h:mm A' : 'MMM D, h A';
  }

  if (hasIsoDayMatch && allFirstOfMonth) {
    return 'MMM YYYY';
  }

  return 'MMM D, YYYY';
}

// ---------------------------------------------------------------------------
// Row coercion helpers
// ---------------------------------------------------------------------------

/**
 * Coerces a row value to a number, defaulting to 0 for null/undefined/NaN.
 * Handles Python's decimal.Decimal serialization artifact where the backend
 * sends `{ __reduce__: [{ "py/type": "decimal.Decimal" }, ["18.75"]] }`
 * instead of a plain number.
 */
function toNumber(value: unknown): number {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    if (Array.isArray(obj.__reduce__) && Array.isArray(obj.__reduce__[1])) {
      const numStr = obj.__reduce__[1][0];
      if (typeof numStr === 'string') {
        const n = Number(numStr);
        return Number.isFinite(n) ? n : 0;
      }
    }
    return 0;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Coerces a row value to a string, using empty string for null/undefined. */
function toLabel(value: unknown): string {
  if (value == null) {
    return '';
  }
  return String(value);
}

/**
 * Formats a value for the x-axis.
 * When the axis is typed as `date`, formats with agency timezone.
 * The date format is auto-detected from the data granularity (hourly,
 * daily, monthly) and passed in via `options.dateFormat`.
 */
function formatAxisValue(
  value: unknown,
  axis: ChartAxis | undefined,
  options: {agencyTimezone?: string; language?: string; dateFormat?: string},
): string {
  if (value == null) {
    return '';
  }
  if (axis?.type === 'date') {
    return formatToCustomDateString(
      String(value),
      options.agencyTimezone,
      options.dateFormat ?? 'MMM D, YYYY',
      options.language,
    );
  }
  return toLabel(value);
}

// ---------------------------------------------------------------------------
// Series types
// ---------------------------------------------------------------------------

export type CategoricalSeriesPoint = {name: string; y: number};
export type CategoricalSeries = {
  name: string;
  data: CategoricalSeriesPoint[];
};

export type ProportionSeries = {
  name?: string;
  data: CategoricalSeriesPoint[];
};

export type FlowLink = {from: string; to: string; weight: number};
export type FlowNode = {id: string; name: string};
export type FlowSeries = {data: FlowLink[]; nodes: FlowNode[]};

export type BuildSeriesOptions = {
  agencyTimezone?: string;
  language?: string;
};

// ---------------------------------------------------------------------------
// Transformations
// ---------------------------------------------------------------------------

/**
 * Categorical charts (column, line, stackedBar, spider).
 * One series per y_axis entry. Rows with null x-axis value are filtered out.
 */
export function buildCategoricalSeries(
  data: ReadonlyArray<Record<string, unknown>>,
  config: ChartConfig,
  options: BuildSeriesOptions = {},
): CategoricalSeries[] {
  const xAxis = normalizeXAxis(config.x_axis);
  const xKey = xAxis?.key;
  const yAxes = config.y_axis ?? [];

  if (!xKey || yAxes.length === 0) {
    return [];
  }

  const validRows = data.filter((row) => row[xKey] != null);

  const dateFormat =
    xAxis?.type === 'date' ? detectDateFormat(validRows, xKey) : undefined;

  return yAxes.map((series) => ({
    name: series.label,
    data: validRows.map((row) => ({
      name: formatAxisValue(row[xKey], xAxis, {...options, dateFormat}),
      y: toNumber(row[series.key]),
    })),
  }));
}

/**
 * Proportion charts (donut, funnel).
 * Single series — each row becomes one slice. x_axis.key = slice name,
 * y_axis[0].key = slice value.
 */
export function buildProportionSeries(
  data: ReadonlyArray<Record<string, unknown>>,
  config: ChartConfig,
  options: BuildSeriesOptions = {},
): ProportionSeries[] {
  const xAxis = normalizeXAxis(config.x_axis);
  const xKey = xAxis?.key;
  const yAxes = config.y_axis ?? [];

  if (!xKey || yAxes.length === 0) {
    return [];
  }

  const valueKey = yAxes[0].key;
  const validRows = data.filter((row) => row[xKey] != null);

  const dateFormat =
    xAxis?.type === 'date' ? detectDateFormat(validRows, xKey) : undefined;

  return [
    {
      name: yAxes[0].label,
      data: validRows.map((row) => ({
        name: formatAxisValue(row[xKey], xAxis, {...options, dateFormat}),
        y: toNumber(row[valueKey]),
      })),
    },
  ];
}

/**
 * Flow charts (sankey). Rows must have `from`, `to`, and optional `weight`.
 * Nodes are auto-derived from unique `from`/`to` values.
 */
export function buildFlowSeries(
  data: ReadonlyArray<Record<string, unknown>>,
): FlowSeries[] {
  const links: FlowLink[] = data
    .filter((row) => row.from != null && row.to != null)
    .map((row) => ({
      from: toLabel(row.from),
      to: toLabel(row.to),
      weight: toNumber(row.weight),
    }));

  const nodeIds = new Set<string>();
  for (const link of links) {
    nodeIds.add(link.from);
    nodeIds.add(link.to);
  }
  const nodes: FlowNode[] = Array.from(nodeIds).map((id) => ({id, name: id}));

  return [{data: links, nodes}];
}
